import { useCallback, useEffect, useRef } from 'react';
import { TextInput } from '@mantine/core';
import axios from 'axios';
import { useIntl } from 'react-intl';

import { freightFormMessages } from './freight-form-messages';
import { getCityStateFromPostalCode } from '../../../../api/address.api';

import type { FreightBrowserDestination } from '../../freight-browser-types';
import type { ChangeEvent } from 'react';

type ShipToSectionProps = {
  value: FreightBrowserDestination;
  onChange: (next: FreightBrowserDestination) => void;
};

const POSTAL_CODE_LOOKUP_DEBOUNCE_MS = 300;
// Freight browser only supports US ship-to today (country field is hidden), matching the lookup below.
const US_POSTAL_CODE_PATTERN = /^\d{5}(-\d{4})?$/;

type AutofilledCityState = { city: string; state: string };

export const ShipToSection = ({ value, onChange }: ShipToSectionProps) => {
  const intl = useIntl();

  // Debounced postal-code lookup reads the latest value/onChange via refs rather than the
  // render's closures, since the lookup resolves well after the render that scheduled it.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // The city/state we last wrote in via lookup, so a later lookup can safely replace them
  // without clobbering anything the merchant typed by hand.
  const lastAutofilledRef = useRef<AutofilledCityState | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      abortControllerRef.current?.abort();
    };
  }, []);

  const applyLookupResult = useCallback((postalCode: string, city: string, state: string) => {
    const currentValue = valueRef.current;
    if (currentValue.postalCode.trim() !== postalCode) {
      // The zip changed again since this lookup was kicked off; a newer lookup (or none) owns the fields.
      return;
    }

    const last = lastAutofilledRef.current;
    const canFillCity = currentValue.city.trim() === '' || currentValue.city === last?.city;
    const canFillState = currentValue.state.trim() === '' || currentValue.state === last?.state;
    if (!canFillCity && !canFillState) {
      return;
    }

    const nextCity = canFillCity ? city : currentValue.city;
    const nextState = canFillState ? state : currentValue.state;

    lastAutofilledRef.current = { city: nextCity, state: nextState };
    onChangeRef.current({ ...currentValue, city: nextCity, state: nextState });
  }, []);

  const runLookup = useCallback(
    (postalCode: string) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      getCityStateFromPostalCode(postalCode, 'US', controller.signal)
        .then((result) => {
          if (!result || controller.signal.aborted) return;
          applyLookupResult(postalCode, result.city, result.state);
        })
        .catch((error) => {
          // Silent by design: city/state have no visible inputs, so there's nothing to show for
          // a miss. Only swallow real failures here — a cancellation isn't one.
          if (axios.isCancel(error)) return;
        });
    },
    [applyLookupResult],
  );

  const handlePostalCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const postalCode = event.currentTarget.value;
    onChange({ ...value, postalCode });

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    abortControllerRef.current?.abort();

    const trimmed = postalCode.trim();
    if (!US_POSTAL_CODE_PATTERN.test(trimmed)) return;
    debounceTimerRef.current = setTimeout(() => runLookup(trimmed), POSTAL_CODE_LOOKUP_DEBOUNCE_MS);
  };

  return (
    <TextInput
      data-testid='address-field-postalCode'
      label={intl.formatMessage(freightFormMessages.postalCodeLabel)}
      placeholder={intl.formatMessage(freightFormMessages.postalCodePlaceholder)}
      value={value.postalCode}
      onChange={handlePostalCodeChange}
      withAsterisk
    />
  );
};
